import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

// Direct module import: the shared UI barrel drags the api client into
// every renderer that needs one primitive.
import { Icon } from '@/shared/ui/Icon/Icon';
import { VideoPlayer } from '@/shared/ui/VideoPlayer/VideoPlayer';

import { i18n } from '../config';
import {
  closeKbVideo,
  getKbVideoHostElement,
  openKnowledgeBase,
  playKbVideo,
  useKbFloatingBox,
  useKbVideoSession,
} from '../model';

import styles from './KnowledgeBase.module.scss';

const RESIZE_CORNERS = ['nw', 'ne', 'sw', 'se'] as const;

/**
 * The one video player of the knowledge base.
 *
 * It is mounted at the app root and rendered into a host element that the
 * article moves into its own slot. Closing the panel or opening another page
 * moves that element to the app corner instead of destroying it, so the
 * video keeps playing from the same second.
 */
export const KbVideoHost = () => {
  const intl = useIntl();
  const session = useKbVideoSession();
  // Created once, kept across renders: the portal target must be stable.
  const [host] = useState(getKbVideoHostElement);
  const detached = session?.detached ?? false;
  const { dragHandlers, resizeHandlers } = useKbFloatingBox(host, detached);

  // A video the reader started from its article slot plays as soon as the
  // player carries its file.
  const autoplay = session?.autoplay ?? false;
  const src = session?.src;

  useEffect(() => {
    if (!autoplay || detached) return;

    playKbVideo();
  }, [autoplay, detached, src]);

  if (!session) return null;

  return createPortal(
    <div
      className={session.detached ? styles.floatingVideo : undefined}
      data-component="knowledge-base-video"
      {...(session.detached ? dragHandlers : {})}
    >
      {/* The corner card has no bar of its own: its actions sit over the
          picture, top right, where they take no height from the video. */}
      {session.detached && (
        <div className={styles.floatingVideoActions}>
          {session.article && (
            <button
              type="button"
              className={styles.floatingVideoBack}
              onClick={() =>
                openKnowledgeBase({
                  slug: session.article!.slug,
                  market: session.article!.market,
                })
              }
            >
              <FormattedMessage {...i18n.backToPage} />
            </button>
          )}
          <button
            type="button"
            className={styles.floatingVideoClose}
            aria-label={intl.formatMessage(i18n.close)}
            onClick={closeKbVideo}
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      )}
      {/* Four invisible corners: the cursor says what each one does. */}
      {session.detached &&
        RESIZE_CORNERS.map((corner) => (
          <div
            key={corner}
            className={styles.floatingResize}
            data-corner={corner}
            {...resizeHandlers}
          />
        ))}
      {/* No player title: the article embed carries its own caption, and
          the corner card has its own bar. */}
      <VideoPlayer
        src={session.src}
        poster={session.poster}
        controlsLabel={intl.formatMessage(i18n.playbackControls)}
        compact={session.detached}
      />
    </div>,
    host,
  );
};
